/**
 * OrderTicket — the buy/sell window, modelled on the Kotak Neo order pad.
 *
 * Pick an instrument (or arrive with one pre-filled from Market Watch), see
 * its live LTP, choose side / product / order type with segmented controls,
 * step quantity in lots for F&O, preview the margin the broker will block,
 * and submit. The active broker connection is resolved automatically — no
 * UUID pasting. Submitted orders still pass the risk engine on the backend.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Minus, Pencil, Plus, Send, Wallet } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, getErrorMessage } from '@/config/api';
import { cn } from '@/lib/cn';
import { formatINR, formatNumber, formatPct, formatQty, signClass, toNum } from '@/lib/format';
import { Alert, Badge, Button, Field, Input, Modal, Select, Switch } from '@/components/ui';
import { InstrumentSearch } from '@/features/market/components/InstrumentSearch';
import { isDerivativeSegment } from '@/features/market/instruments';
import type { InstrumentResult } from '@/features/market/components/InstrumentSearch';

// ── Types ───────────────────────────────────────────────────────────────────

export interface OrderTicketPrefill {
  exchange_segment?: string;
  trading_symbol?: string;
  instrument_token?: string;
  transaction_type?: 'B' | 'S';
  lot_size?: number;
  product?: 'CNC' | 'MIS' | 'NRML';
  ltp?: string | number;
  /** Starting quantity (e.g. the net quantity of a position being exited). */
  quantity?: number;
}

export interface PlaceOrderResult {
  order_id: string;
  status: string;
  broker_order_id: string;
  risk_rejected: boolean;
  violations: string[];
  warnings: string[];
  connection_id: string;
  message: string;
}

interface Instrument {
  exchange_segment: string;
  trading_symbol: string;
  instrument_token: string;
  lot_size: number;
  name?: string;
}

interface BrokerStatus {
  connection_id: string;
  display_name: string;
  is_active: boolean;
  is_session_valid: boolean;
}

interface LiveQuote {
  ltp: string;
  change: string;
  change_percent: string;
  open_interest: string;
  best_bid_price: string;
  best_ask_price: string;
  high: string;
  low: string;
}

interface MarginPreview {
  required_margin: string;
  available_margin: string;
  is_sufficient: boolean;
  shortfall: string;
}

interface OrderTicketProps {
  open: boolean;
  onClose: () => void;
  prefill?: OrderTicketPrefill | null;
  onPlaced?: (result: PlaceOrderResult) => void;
}

// ── Schema (payload contract for POST /orders) ──────────────────────────────

const ticketSchema = z
  .object({
    transaction_type: z.enum(['B', 'S']),
    order_type: z.enum(['MKT', 'L', 'SL', 'SL-M']),
    product: z.enum(['CNC', 'MIS', 'NRML']),
    quantity: z.coerce.number().int().positive('Quantity must be at least 1'),
    price: z.coerce.number().min(0).default(0),
    trigger_price: z.coerce.number().min(0).default(0),
    validity: z.enum(['DAY', 'IOC']).default('DAY'),
    disclosed_quantity: z.coerce.number().int().min(0).default(0),
    is_amo: z.boolean().default(false),
  })
  .refine((d) => !['L', 'SL'].includes(d.order_type) || d.price > 0, {
    message: 'Limit price is required',
    path: ['price'],
  })
  .refine((d) => !['SL', 'SL-M'].includes(d.order_type) || d.trigger_price > 0, {
    message: 'Trigger price is required for stop-loss orders',
    path: ['trigger_price'],
  });

type TicketForm = z.infer<typeof ticketSchema>;

const ORDER_TYPES: Array<{ value: TicketForm['order_type']; label: string }> = [
  { value: 'MKT', label: 'Market' },
  { value: 'L', label: 'Limit' },
  { value: 'SL', label: 'SL' },
  { value: 'SL-M', label: 'SL-M' },
];

const PRODUCTS: Array<{ value: TicketForm['product']; label: string; hint: string; cashOnly?: boolean }> = [
  { value: 'MIS', label: 'MIS', hint: 'Intraday · auto square-off' },
  { value: 'CNC', label: 'CNC', hint: 'Delivery', cashOnly: true },
  { value: 'NRML', label: 'NRML', hint: 'Carry forward' },
];

/** Segmented control shared by side / product / order type. */
function Segmented<T extends string>({
  value,
  options,
  onChange,
  tone,
  'aria-label': ariaLabel,
}: {
  value: T;
  options: Array<{ value: T; label: string; disabled?: boolean }>;
  onChange: (v: T) => void;
  tone?: (v: T) => 'buy' | 'sell' | undefined;
  'aria-label': string;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex rounded-lg border border-line-strong bg-surface-2 p-0.5">
      {options.map((opt) => {
        const active = opt.value === value;
        const t = tone?.(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={opt.disabled}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
              active
                ? t === 'buy'
                  ? 'bg-buy text-white shadow-raised'
                  : t === 'sell'
                    ? 'bg-sell text-white shadow-raised'
                    : 'bg-surface-3 text-fg shadow-raised'
                : 'text-fg-subtle hover:text-fg'
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function OrderTicket({ open, onClose, prefill, onPlaced }: OrderTicketProps) {
  const [instrument, setInstrument] = useState<Instrument | null>(null);
  const [pickingInstrument, setPickingInstrument] = useState(false);
  const [connections, setConnections] = useState<BrokerStatus[]>([]);
  const [availableMargin, setAvailableMargin] = useState<string | null>(null);
  const [quote, setQuote] = useState<LiveQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [margin, setMargin] = useState<MarginPreview | null>(null);
  const [marginError, setMarginError] = useState<string | null>(null);
  const [marginLoading, setMarginLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string; warnings?: string[] } | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const priceTouchedRef = useRef(false);

  const activeConnection = useMemo(
    () => connections.find((c) => c.is_active && c.is_session_valid) ?? null,
    [connections]
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TicketForm>({
    resolver: zodResolver(ticketSchema) as never,
    defaultValues: {
      transaction_type: 'B',
      order_type: 'MKT',
      product: 'MIS',
      quantity: 1,
      price: 0,
      trigger_price: 0,
      validity: 'DAY',
      disclosed_quantity: 0,
      is_amo: false,
    },
  });

  const side = watch('transaction_type');
  const orderType = watch('order_type');
  const product = watch('product');
  const quantity = watch('quantity');
  const price = watch('price');
  const triggerPrice = watch('trigger_price');
  const isAmo = watch('is_amo');

  const derivative = instrument ? isDerivativeSegment(instrument.exchange_segment) : false;
  const lotSize = instrument?.lot_size && instrument.lot_size > 1 ? instrument.lot_size : 1;
  const needsPrice = orderType === 'L' || orderType === 'SL';
  const needsTrigger = orderType === 'SL' || orderType === 'SL-M';
  const ltp = toNum(quote?.ltp);
  const effectivePrice = needsPrice && toNum(price) > 0 ? toNum(price) : ltp;
  const orderValue = effectivePrice * toNum(quantity);

  // ── Open: apply prefill, load broker connection + funds ───────────────

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setMargin(null);
    setMarginError(null);
    setQuote(null);
    setQuoteError(null);
    priceTouchedRef.current = false;

    if (prefill?.instrument_token && prefill.trading_symbol) {
      setInstrument({
        exchange_segment: prefill.exchange_segment || 'nse_cm',
        trading_symbol: prefill.trading_symbol,
        instrument_token: prefill.instrument_token,
        lot_size: prefill.lot_size ?? 1,
      });
      setPickingInstrument(false);
      if (prefill.ltp) {
        setQuote({
          ltp: String(prefill.ltp), change: '0', change_percent: '0', open_interest: '0',
          best_bid_price: '0', best_ask_price: '0', high: '0', low: '0',
        });
      }
    } else {
      setInstrument(null);
      setPickingInstrument(true);
    }

    const isFo = isDerivativeSegment(prefill?.exchange_segment || 'nse_cm');
    reset({
      transaction_type: prefill?.transaction_type ?? 'B',
      order_type: 'MKT',
      product: prefill?.product ?? (isFo ? 'NRML' : 'MIS'),
      quantity: prefill?.quantity && prefill.quantity > 0
        ? prefill.quantity
        : prefill?.lot_size && prefill.lot_size > 1
          ? prefill.lot_size
          : 1,
      price: 0,
      trigger_price: 0,
      validity: 'DAY',
      disclosed_quantity: 0,
      is_amo: false,
    });

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/brokers/status');
        if (cancelled) return;
        setConnections(data);
        const active = (data as BrokerStatus[]).find((c) => c.is_active && c.is_session_valid);
        if (active) {
          try {
            const funds = await api.get('/brokers/funds', { params: { connection_id: active.connection_id } });
            if (!cancelled) setAvailableMargin(String(funds.data.available_margin ?? funds.data.available_cash ?? ''));
          } catch {
            if (!cancelled) setAvailableMargin(null);
          }
        }
      } catch {
        if (!cancelled) setConnections([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, prefill, reset]);

  // ── Live quote polling while the ticket is open ────────────────────────

  const fetchQuote = useCallback(async (inst: Instrument) => {
    try {
      const { data } = await api.post('/market/quotes/live', {
        instruments: [{ instrument_token: inst.instrument_token, exchange_segment: inst.exchange_segment }],
      });
      const q = Array.isArray(data) ? data[0] : null;
      if (q) {
        setQuote({
          ltp: String(q.ltp ?? '0'),
          change: String(q.chg ?? q.change ?? '0'),
          change_percent: String(q.chgp ?? q.change_percent ?? '0'),
          open_interest: String(q.oi ?? q.open_interest ?? '0'),
          best_bid_price: String(q.bp ?? q.best_bid_price ?? '0'),
          best_ask_price: String(q.ap ?? q.best_ask_price ?? '0'),
          high: String(q.h ?? q.high ?? '0'),
          low: String(q.l ?? q.low ?? '0'),
        });
        setQuoteError(null);
      }
    } catch (err) {
      setQuoteError(getErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    if (!open || !instrument) return;
    fetchQuote(instrument);
    const timer = setInterval(() => fetchQuote(instrument), 4_000);
    return () => clearInterval(timer);
  }, [open, instrument, fetchQuote]);

  // Pre-fill the limit price from LTP the first time a priced order type is chosen.
  useEffect(() => {
    if (!needsPrice || priceTouchedRef.current || ltp <= 0) return;
    if (toNum(price) === 0) setValue('price', Number(ltp.toFixed(2)));
  }, [needsPrice, ltp, price, setValue]);

  // ── Margin preview (debounced) ────────────────────────────────────────

  useEffect(() => {
    if (!open || !instrument || !activeConnection || toNum(quantity) <= 0) {
      setMargin(null);
      return;
    }
    if (needsPrice && toNum(price) <= 0) return;
    const handle = setTimeout(async () => {
      setMarginLoading(true);
      try {
        const { data } = await api.post('/orders/margin', {
          connection_id: activeConnection.connection_id,
          exchange_segment: instrument.exchange_segment,
          trading_symbol: instrument.trading_symbol,
          instrument_token: instrument.instrument_token,
          transaction_type: side,
          order_type: orderType,
          product,
          quantity: toNum(quantity),
          price: needsPrice ? toNum(price) : 0,
          trigger_price: needsTrigger ? toNum(triggerPrice) : 0,
        });
        setMargin(data);
        setMarginError(null);
      } catch (err) {
        setMargin(null);
        setMarginError(getErrorMessage(err));
      } finally {
        setMarginLoading(false);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [open, instrument, activeConnection, side, orderType, product, quantity, price, triggerPrice, needsPrice, needsTrigger]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const selectInstrument = (r: InstrumentResult) => {
    const lots = r.lot_size && r.lot_size > 1 ? r.lot_size : 1;
    setInstrument({
      exchange_segment: r.exchange_segment,
      trading_symbol: r.trading_symbol,
      instrument_token: r.instrument_token,
      lot_size: lots,
      name: r.name,
    });
    setPickingInstrument(false);
    setQuote(null);
    setValue('quantity', lots);
    if (isDerivativeSegment(r.exchange_segment) && product === 'CNC') setValue('product', 'NRML');
    priceTouchedRef.current = false;
    setValue('price', 0);
  };

  const stepQty = (dir: 1 | -1) => {
    const next = Math.max(lotSize, toNum(quantity) + dir * lotSize);
    setValue('quantity', next, { shouldValidate: true });
  };

  const onSubmit = async (data: TicketForm) => {
    if (!instrument) {
      setResult({ ok: false, message: 'Pick an instrument first.' });
      return;
    }
    setIsSubmitting(true);
    setResult(null);
    try {
      const payload = {
        connection_id: activeConnection?.connection_id ?? null,
        exchange_segment: instrument.exchange_segment,
        trading_symbol: instrument.trading_symbol,
        instrument_token: instrument.instrument_token,
        transaction_type: data.transaction_type,
        order_type: data.order_type,
        product: data.product,
        quantity: data.quantity,
        price: needsPrice ? data.price : 0,
        trigger_price: needsTrigger ? data.trigger_price : 0,
        validity: data.validity,
        disclosed_quantity: data.disclosed_quantity,
        is_amo: data.is_amo,
      };
      const res = await api.post<PlaceOrderResult>('/orders', payload);
      const out = res.data;
      if (out.risk_rejected) {
        setResult({ ok: false, message: `Risk engine rejected: ${(out.violations || []).join(', ')}` });
      } else if (out.status === 'rejected') {
        setResult({ ok: false, message: out.message || 'Broker rejected the order' });
      } else {
        setResult({
          ok: true,
          message: `${data.transaction_type === 'B' ? 'Buy' : 'Sell'} ${formatQty(data.quantity)} ${instrument.trading_symbol} sent · broker #${out.broker_order_id || '—'}`,
          warnings: out.warnings,
        });
        onPlaced?.(out);
      }
    } catch (err) {
      setResult({ ok: false, message: getErrorMessage(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const changeDir = toNum(quote?.change);
  const ChangeIcon = changeDir > 0 ? ArrowUp : changeDir < 0 ? ArrowDown : Minus;
  const lots = lotSize > 1 ? Math.round(toNum(quantity) / lotSize) : 0;
  const qtyOffLot = lotSize > 1 && toNum(quantity) % lotSize !== 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={instrument ? `${side === 'B' ? 'Buy' : 'Sell'} ${instrument.trading_symbol}` : 'New order'}
      description="Orders pass the risk engine, then go to your broker."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            type="submit"
            form="order-ticket-form"
            variant={side === 'B' ? 'primary' : 'danger'}
            loading={isSubmitting}
            disabled={!instrument}
            icon={Send}
            className={cn(side === 'B' && 'bg-buy hover:bg-buy/90 active:bg-buy/80')}
          >
            {side === 'B' ? 'Place buy' : 'Place sell'}
            {toNum(quantity) > 0 && instrument ? ` · ${formatQty(quantity)}` : ''}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {result && (
          <Alert tone={result.ok ? 'success' : 'danger'} onDismiss={() => setResult(null)}>
            <span>{result.message}</span>
            {result.ok && result.warnings && result.warnings.length > 0 && (
              <ul className="mt-1 list-disc pl-4 text-xs opacity-90">
                {result.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        {/* Broker + funds strip */}
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs">
          <div className="flex items-center gap-2">
            {activeConnection ? (
              <Badge tone="success">{activeConnection.display_name}</Badge>
            ) : (
              <Badge tone="danger">No broker session</Badge>
            )}
            {!activeConnection && <span className="text-fg-subtle">Connect in Settings → Broker to send orders.</span>}
          </div>
          <div className="flex items-center gap-1.5 text-fg-muted">
            <Wallet className="h-3.5 w-3.5 text-fg-faint" aria-hidden="true" />
            <span>Available</span>
            <span className="font-tabular text-fg">{availableMargin ? formatINR(availableMargin) : '—'}</span>
          </div>
        </div>

        {/* Instrument */}
        {pickingInstrument || !instrument ? (
          <div className="rounded-lg border border-line p-3">
            <p className="mb-2 text-xs font-medium text-fg-muted">Instrument</p>
            <InstrumentSearch
              onSelect={selectInstrument}
              actionLabel="Trade"
              defaultSegment={instrument?.exchange_segment || prefill?.exchange_segment || 'nse_cm'}
            />
            {instrument && (
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => setPickingInstrument(false)}>
                Keep {instrument.trading_symbol}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-line p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate text-base font-semibold text-fg">{instrument.trading_symbol}</p>
                <Badge tone="neutral" className="uppercase">
                  {instrument.exchange_segment.replace('_', ' ')}
                </Badge>
                {lotSize > 1 && <Badge tone="brand">Lot {lotSize}</Badge>}
              </div>
              <p className="font-tabular text-xs text-fg-faint">
                Token {instrument.instrument_token}
                {instrument.name ? ` · ${instrument.name}` : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="font-tabular text-xl font-semibold text-fg">{quote ? formatINR(quote.ltp) : '—'}</p>
              {quote && (
                <p className={cn('inline-flex items-center gap-1 font-tabular text-xs', signClass(quote.change))}>
                  <ChangeIcon className="h-3 w-3" aria-hidden="true" />
                  {formatNumber(quote.change)} ({formatPct(quote.change_percent)})
                </p>
              )}
              {quote && derivative && toNum(quote.open_interest) > 0 && (
                <p className="font-tabular text-xs text-fg-subtle">OI {formatQty(quote.open_interest)}</p>
              )}
              {quoteError && !quote && <p className="text-xs text-danger">Quote unavailable</p>}
            </div>
            <div className="flex w-full items-center justify-between gap-2 border-t border-line pt-2 text-xs text-fg-subtle">
              <span className="font-tabular">
                {quote && toNum(quote.best_bid_price) > 0 && (
                  <>
                    Bid <span className="text-buy">{formatNumber(quote.best_bid_price)}</span> · Ask{' '}
                    <span className="text-sell">{formatNumber(quote.best_ask_price)}</span>
                  </>
                )}
              </span>
              <Button variant="ghost" size="sm" icon={Pencil} onClick={() => setPickingInstrument(true)}>
                Change
              </Button>
            </div>
          </div>
        )}

        <form id="order-ticket-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-fg-muted">Side</span>
              <Segmented
                aria-label="Side"
                value={side}
                options={[
                  { value: 'B', label: 'Buy' },
                  { value: 'S', label: 'Sell' },
                ]}
                tone={(v) => (v === 'B' ? 'buy' : 'sell')}
                onChange={(v) => setValue('transaction_type', v)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-fg-muted">Product</span>
              <Segmented
                aria-label="Product"
                value={product}
                options={PRODUCTS.map((p) => ({ value: p.value, label: p.label, disabled: Boolean(p.cashOnly && derivative) }))}
                onChange={(v) => setValue('product', v)}
              />
              <span className="text-xs text-fg-faint">{PRODUCTS.find((p) => p.value === product)?.hint}</span>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-fg-muted">Order type</span>
            <Segmented
              aria-label="Order type"
              value={orderType}
              options={ORDER_TYPES}
              onChange={(v) => {
                setValue('order_type', v);
                if ((v === 'SL' || v === 'SL-M') && toNum(triggerPrice) === 0 && ltp > 0) {
                  setValue('trigger_price', Number(ltp.toFixed(2)));
                }
              }}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field
              label={lotSize > 1 ? `Quantity (${lots} lot${lots === 1 ? '' : 's'})` : 'Quantity'}
              error={errors.quantity?.message ?? (qtyOffLot ? `Must be a multiple of ${lotSize}` : undefined)}
              required
            >
              {(field) => (
                <div className="flex gap-1">
                  <Button variant="secondary" size="icon" aria-label="Decrease quantity" onClick={() => stepQty(-1)}>
                    <Minus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Input {...field} type="number" min={1} step={lotSize} mono className="text-center" {...register('quantity')} />
                  <Button variant="secondary" size="icon" aria-label="Increase quantity" onClick={() => stepQty(1)}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </Field>

            <Field label="Price" error={errors.price?.message} hint={needsPrice ? undefined : 'At market'}>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  step="0.05"
                  min={0}
                  mono
                  disabled={!needsPrice}
                  {...register('price', { onChange: () => { priceTouchedRef.current = true; } })}
                />
              )}
            </Field>

            <Field label="Trigger" error={errors.trigger_price?.message} hint={needsTrigger ? undefined : 'SL / SL-M only'}>
              {(field) => (
                <Input {...field} type="number" step="0.05" min={0} mono disabled={!needsTrigger} {...register('trigger_price')} />
              )}
            </Field>
          </div>

          {/* Value + margin */}
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs sm:grid-cols-4">
            <div>
              <p className="text-fg-faint">Order value</p>
              <p className="font-tabular text-fg">{orderValue > 0 ? formatINR(orderValue) : '—'}</p>
            </div>
            <div>
              <p className="text-fg-faint">Margin required</p>
              <p className="font-tabular text-fg">
                {marginLoading ? '…' : margin ? formatINR(margin.required_margin) : '—'}
              </p>
            </div>
            <div>
              <p className="text-fg-faint">Available</p>
              <p className="font-tabular text-fg">
                {margin ? formatINR(margin.available_margin) : availableMargin ? formatINR(availableMargin) : '—'}
              </p>
            </div>
            <div>
              <p className="text-fg-faint">Status</p>
              {margin ? (
                <Badge tone={margin.is_sufficient ? 'success' : 'danger'}>
                  {margin.is_sufficient ? 'Sufficient' : `Short ${formatINR(margin.shortfall)}`}
                </Badge>
              ) : (
                <span className="text-fg-subtle">{marginError ? 'Preview unavailable' : '—'}</span>
              )}
            </div>
          </div>

          {/* Advanced */}
          <div className="flex flex-col gap-3">
            <button
              type="button"
              className="self-start text-xs text-fg-subtle transition-colors hover:text-fg-muted"
              onClick={() => setShowAdvanced((v) => !v)}
              aria-expanded={showAdvanced}
            >
              {showAdvanced ? 'Hide' : 'Show'} validity, AMO and disclosed quantity
            </button>
            {showAdvanced && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Validity" error={errors.validity?.message}>
                  {(field) => (
                    <Select {...field} {...register('validity')}>
                      <option value="DAY">DAY</option>
                      <option value="IOC">IOC</option>
                    </Select>
                  )}
                </Field>
                <Field label="Disclosed qty" error={errors.disclosed_quantity?.message}>
                  {(field) => <Input {...field} type="number" min={0} mono {...register('disclosed_quantity')} />}
                </Field>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-fg-muted">After-market order</span>
                  <Switch checked={isAmo} onChange={(v) => setValue('is_amo', v)} label={isAmo ? 'AMO on' : 'AMO off'} />
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
}
